/** Audio initialization — loads WASM, creates AudioContext, and registers processors.
 *
 * This is the single entry point for bootstrapping the audio system.
 * Call `initAudio()` once on user gesture, then pass the result to
 * any classes that need the AudioContext or WASM node constructors.
 */

import * as LazyContext from "can-start-audio-context";
import PKG_WASM_URL from "./pkg/rs_wasm_ts_worklet_bg.wasm";

/**
 * The wasm-bindgen JS module must be loaded unbundled so that
 * `import.meta.url` inside it points to the raw file URL.
 * The AudioWorklet thread uses that URL to `import { initSync }` from
 * the same file — which only works if it isn't inlined by the bundler.
 */

const PKG_JS_URL = "./pkg/rs_wasm_ts_worklet.js";
export type WasmWorkletModule = typeof import("./pkg/rs_wasm_ts_worklet.js");

/** Preferred AudioContext options for the synth engine. */
const AUDIO_CONTEXT_OPTIONS: AudioContextOptions = {
	sampleRate: 48_000,
	latencyHint: "interactive",
} as const;

/** WASM node constructor types. */
export type WasmOscillatorNode = {
	readonly node: AudioWorkletNode;
	free(): void;
};

export type WasmFilterNode = {
	readonly node: AudioWorkletNode;
	free(): void;
};

export type WasmEnvelopeNode = {
	readonly node: AudioWorkletNode;
	free(): void;
};

/** The subset of the WASM module used at runtime. */
export type WasmNodeFactory = {
	readonly OscillatorNode: new (
		ctx: AudioContext,
		frequency: number,
	) => WasmOscillatorNode;
	readonly FilterNode: new (
		ctx: AudioContext,
		cutoff: number,
		resonance: number,
	) => WasmFilterNode;
	readonly EnvelopeNode: new (ctx: AudioContext) => WasmEnvelopeNode;
};

/** Result of audio initialization — everything downstream needs. */
export type AudioEnv = {
	readonly context: AudioContext;
	readonly factory: WasmNodeFactory;
};

/** Load WASM, create & register AudioContext, and ensure it is running.
 *
 * Must be called in response to a user gesture (click / keypress)
 * so the browser allows the AudioContext to start.
 */
export async function initAudio(): Promise<AudioEnv> {
	// 1. Dynamic import keeps the wasm-bindgen JS unbundled so import.meta.url
	//    resolves to /pkg/rs_wasm_ts_worklet.js — required by AudioWorklet threads.
	const PKG_JS_PROMISE = import(`${PKG_JS_URL}`);
	const wasmModule: WasmWorkletModule = await PKG_JS_PROMISE;
	const initWasm = wasmModule.default;
	const { registerContextWith, OscillatorNode, FilterNode, EnvelopeNode } =
		wasmModule;

	// 2. Load WASM binary from the static /pkg/ route
	const initWasmPromise = initWasm({ module_or_path: PKG_WASM_URL });

	const tsContext = new AudioContext(AUDIO_CONTEXT_OPTIONS);

	// Wait for both the WASM module and the AudioContext to be ready.
	await initWasmPromise;
	const context = await registerContextWith(tsContext);

	// 4. Ensure the context is running (handles autoplay policies)
	await LazyContext.start(context);

	return {
		context,
		factory: {
			OscillatorNode,
			FilterNode,
			EnvelopeNode,
		},
	};
}
